import React, { useState, useEffect, useRef } from 'react';
import { Avatar } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { message } from '@/components/common/Message';
import { selectIsTeamSpace } from '@/store/selectors/spaceSelectors';
import ExpertSkillsNavIcon from '@/components/common/icons/ExpertSkillsNavIcon';
import { hasToken, getUserInfo, clearAllUserData, maskMobile } from '@/utils/auth';
import authService from '@/services/amai/auth.service';
import { useRoutePreloader } from '@/hooks/useRoutePreloader';
import { eventBus } from '@/utils/eventBus';
import { AGENT_BASE_URL, ENABLE_AI_MANGA } from '@/config/agent';
import { versionService } from '@/services';
import { useBrand } from '@/contexts/BrandContext';
import { useLanguage } from '@/contexts/LanguageContext';
import i18n from '@/i18n/config';
import AccountMenu from '@/components/business/AccountMenu';

// 导入自定义SVG图标
import NewHomeIcon from '@/assets/icons/home-icon-new.svg?url';
import ActiveHomeIcon from '@/assets/icons/home-icon-active.svg?url';
import ArrowIcon from '@/assets/icons/sidebar-arrow.svg?url';
import InactiveTextIcon from '@/assets/icons/text-icon-inactive.svg?url';
import ActiveTextIcon from '@/assets/icons/text-icon-active.svg?url';
import InactiveAssetsIcon from '@/assets/icons/assets-icon-inactive.svg?url';
import ActiveAssetsIcon from '@/assets/icons/assets-icon-active.svg?url';
import InactiveImageIcon from '@/assets/icons/image-icon-inactive.svg?url';
import ActiveImageIcon from '@/assets/icons/image-icon-active.svg?url';
import InactiveVideoIcon from '@/assets/icons/video-icon-inactive.svg?url';
import ActiveVideoIcon from '@/assets/icons/video-icon-active.svg?url';
import InactiveToolsIcon from '@/assets/icons/tools-icon-inactive.svg?url';
import ActiveToolsIcon from '@/assets/icons/tools-icon-active.svg?url';
// import AvatarIcon from '@/assets/icons/avatar-icon.svg?url';
// import AvatarIcon from '@/assets/images/avatar.png';
import AvatarIcon from '@/assets/icons/icon-profile.svg';
import AssetsIconRicegrainvalue from '@/assets/icons/assets-icon-ricegrainvalue.svg?url';
import MemberIcon from '@/assets/icons/member.svg?url';
import InviteIcon from '@/assets/icons/invite-icon.png';
import UserAvatarIcon from '@/assets/icons/user-avatar.svg?url';
import QAIcon from '@/assets/icons/qa.png';
import QAInputIcon from '@/assets/icons/qa-input.png';
import WeChatQRCode from '@/assets/guide/wechat.png';
import AlarmIcon from '@/assets/icons/alarm.svg?url';
import VideoEdit from '@/assets/icons/video-edit-icon.svg?url';
import VideoEditActive from '@/assets/icons/video-edit-active-icon.svg?url';
import AiAgent from '@/assets/icons/ai.svg?url';


// 用户菜单弹出项样式
const userMenuPopupStyles = {
  container: {
    position: 'absolute' as const,
    bottom: '60px',
    right: '24px',
    width: 'calc(100% - 48px)',
    borderRadius: '10px',
    background: '#2B2B2B',
    zIndex: 1000,
  },
  menuItem: {
    display: 'flex' as const,
    width: '100%',
    height: '40px',
    padding: '0 12px',
    alignItems: 'center' as const,
    gap: '12px',
    cursor: 'pointer' as const,
  },
  icon: {
    width: '28px',
    height: '16px',
    flexShrink: 0,
  },
  avatar: {
    width: '16px',
    height: '16px',
    marginLeft: '6px',
    marginRight: '6px',
  },
  text: {
    color: '#FFF',
    fontFeatureSettings: "'liga' off, 'clig' off",
    fontSize: '14px',
    fontStyle: 'normal' as const,
    fontWeight: 600,
    lineHeight: '16px',
  },
};

/**
 * 协议页面专用侧边栏
 *
 * 特点：
 * 1. 显示所有导航项，但对需要认证的功能进行登录检查
 * 2. 未登录用户点击受保护功能时跳转到登录页
 * 3. 协议相关导航项可以正常访问
 */
const AgreementSidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();
  // 团队空间不展示「邀请好友」入口（与 MainLayout/Sidebar 同规则，20261104）
  const isTeamSpace = useSelector(selectIsTeamSpace);
  // 强制重渲染：RouterProvider 内部不响应外层 Context 变化，需要手动触发
  const [, forceUpdate] = useState(0);
  useEffect(() => {
    const handler = () => forceUpdate(n => n + 1);
    i18n.on('languageChanged', handler);
    return () => { i18n.off('languageChanged', handler); };
  }, []);
  const { preloadRouteWithDelay, cancelPreload, smartPreload } = useRoutePreloader();
  const [creationOpen, setCreationOpen] = useState(true);
  const [userInfo, setUserInfo] = useState<{
    user_id: string;
    user_name: string;
    user_avatar: string;
    mobile?: string;
    auth_identifier?: string;
    show_new_user_gift?:boolean;
  } | null>(() => (hasToken() ? getUserInfo() : null));
  const [isUserHovered, setIsUserHovered] = useState(false);
  const [isFeedbackHovered, setIsFeedbackHovered] = useState(false);
  const { hiddenUserMenuItems = [], hiddenSidebarItems = [], hiddenSidebarSections = [], icpRecord, showLoginRegisterButton } = useBrand();
  const [isLoggedIn, setIsLoggedIn] = useState(() => hasToken());
  const [showVersion, setShowVersion] = useState(false);

  // 三击首页图标显示版本号
  const homeClickCount = useRef(0);
  const homeClickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleHomeClick = () => {
    homeClickCount.current += 1;

    if (homeClickTimer.current) {
      clearTimeout(homeClickTimer.current);
    }

    // 500ms 内重置点击计数
    homeClickTimer.current = setTimeout(() => {
      if (homeClickCount.current >= 3) {
        setShowVersion(true);
      }
      homeClickCount.current = 0;
    }, 500);
  };

  const toggleCreation = () => {
    setCreationOpen(!creationOpen);
  };

  // 检查登录状态并获取用户信息
  useEffect(() => {
    const loggedIn = hasToken();
    setIsLoggedIn(loggedIn);

    if (loggedIn) {
      const localUserInfo = getUserInfo();
      if (localUserInfo) {
        setUserInfo(localUserInfo);
      }
    }

    // 智能预加载相关页面
    smartPreload(location.pathname);

    // 监听用户信息更新事件（由 App 全局触发）
    const handleUserInfoUpdate = (updatedUserInfo: any) => {
      setUserInfo(updatedUserInfo);
      setIsLoggedIn(true);
    };

    eventBus.on('userInfoUpdated', handleUserInfoUpdate);

    return () => {
      eventBus.off('userInfoUpdated', handleUserInfoUpdate);
    };
  }, [smartPreload, location.pathname]);



  const goToRule = () => {
    navigate('/agreement/user-agreement');
  };

  const goToRoute = (url: string) => {
    navigate(url);
  };

  // 处理需要认证的导航点击
  const handleProtectedNavigation = (path: string, _actionName: string, state?: any) => {
    if (!isLoggedIn) {
      eventBus.emit('showLoginModal');
      return;
    }
    if (state) {
      navigate(path, { state });
    } else {
      navigate(path);
    }
  };

  // 定义用于跳转直接导航
  const handleClickJump = () => {
    if (!isLoggedIn) {
      // redirectToLogin(navigate);
      eventBus.emit('showLoginModal');
      return;
    }
    const token = localStorage.getItem('anmo_ai_token');
    const url = new URL(`${AGENT_BASE_URL}/global?key=space`);
    const lang = i18n.language.startsWith('en') ? 'en' : 'zh';
    url.searchParams.set('lang', lang);
    if (token) {
      url.searchParams.set('token', token);
      window.open(url.toString(), '_blank');
    }
  };

  // 处理鼠标悬停预加载
  const handleMouseEnter = (path: string) => {
    if (isLoggedIn) {
      preloadRouteWithDelay(path, 150);
    }
  };

  const handleMouseLeave = () => {
    cancelPreload();
  };

  // 处理退出登录
  const handleLogout = async () => {
    try {
      const response = await authService.logout();
      console.log('🔄 [AgreementSidebar] 退出登录接口调用成功:', response);
      if (response.code === 0) {
        message.success(t('user.logoutSuccess'));
      } else {
        console.warn('退出登录接口调用失败:', response.message);
      }
    } catch (error: any) {
      console.error('退出登录接口调用失败:', error);
    } finally {
      // 清除所有用户数据
      try {
        await clearAllUserData();
        console.log('[AgreementSidebar] 用户数据清理完成');
      } catch (clearError) {
        console.error('[AgreementSidebar] 清理用户数据失败:', clearError);
      }

      setIsLoggedIn(false);
      setUserInfo(null);
      // 使用统一的登录跳转逻辑
      // redirectToLogin(navigate);
      navigate('/home');
    }
  };

  const activeColor = '#FF6A39';
  const inactiveTextColor = 'rgba(255,255,255,0.85)';
  const borderColor = 'rgba(255,255,255,0.06)';

  const isPathActive = (path: string) => {
    if (path === '/home') {
      return location.pathname === path;
    }
    // 精确匹配会员和米粒值页面
    if (path === '/ricegrain/membership' || path === '/ricegrain/purchase') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const isCreationActive = location.pathname.startsWith('/creation');

  return (
    <>
      <div
      className="sidebar-container"
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(calc(var(--vh, 1vh) * 100) - 66px)',
        background: '#101010',
        position: 'relative',
        borderTop: 'none',
      }}
    >
      {/* 菜单内容区域 */}
      <div
        style={{
          flex: 1,
          padding: '8px 16px',
          paddingBottom: '8px',
          overflowY: 'auto',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
        className="sidebar-menu-content"
      >
        {/* 首页（品牌可经 hiddenSidebarItems 隐藏，如 gxzs 站点默认落地任务大厅） */}
        {!hiddenSidebarItems.includes('home') && (
        <div style={{ marginBottom: '8px', position: 'relative' }}>
          <div
            style={{
              position: 'absolute',
              left: '24px',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 2,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <img
              src={isPathActive('/home') ? ActiveHomeIcon : NewHomeIcon}
              alt={t('sidebar.home')}
              onClick={handleHomeClick}
              style={{
                width: '16px',
                height: '16px',
                cursor: 'pointer',
              }}
            />
          </div>

          <div
            onClick={() => handleProtectedNavigation('/home', t('sidebar.home'))}
            onMouseEnter={() => handleMouseEnter('/home')}
            onMouseLeave={handleMouseLeave}
            style={{
              display: 'flex',
              alignItems: 'center',
              width: '197px',
              height: '40px',
              flexShrink: 0,
              borderRadius: '10px',
              background: isPathActive('/home') ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
              padding: '12px 16px 12px 50px',
              boxSizing: 'border-box',
              textDecoration: 'none',
              transition: 'all 0.2s ease',
              cursor: 'pointer',
            }}
          >
            <span
              style={{
                color: isPathActive('/home') ? '#FF6D49' : '#FFF',
                fontFeatureSettings: "'liga' off, 'clig' off",
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 600,
                lineHeight: '16px',
              }}
            >
              {t('sidebar.home')}{showVersion && ` ${versionService.getLocalVersion() !== 'unknown' ? versionService.getLocalVersion() : ''}`}
            </span>
          </div>
        </div>
        )}

        {/* AI创作下拉菜单（品牌可经 hiddenSidebarSections 隐藏，如 gxzs 站点） */}
        {!hiddenSidebarSections.includes('creation') && (
        <div style={{ marginBottom: '8px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '12px 16px 12px 15px',
              cursor: 'pointer',
              borderRadius: '6px',
              transition: 'all 0.2s ease',
            }}
            onClick={toggleCreation}
          >
            <span
              style={{
                color: isCreationActive ? activeColor : '#FFF',
                opacity: isCreationActive ? 1 : 0.5,
                fontFeatureSettings: "'liga' off, 'clig' off",
                fontSize: '12px',
                fontStyle: 'normal',
                fontWeight: isCreationActive ? '600' : '400',
                lineHeight: '16px',
                marginRight: '4px',
              }}
            >
              {t('sidebar.aiCreation')}
            </span>
            <img
              src={ArrowIcon}
              alt="toggle creation"
              style={{
                width: '13px',
                height: '13px',
                flexShrink: 0,
                opacity: isCreationActive ? 1 : 0.5,
                transform: creationOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform 0.3s, opacity 0.2s ease',
              }}
            />
          </div>

          {creationOpen && (
            <div style={{ padding: '0 8px' }}>
              {/* 创意画布（置于文本创作上方） */}
              <div style={{ position: 'relative', marginBottom: '4px' }}>
                <div
                  style={{
                    position: 'absolute',
                    left: '17px',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 2,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <img
                    src={AiAgent}
                    alt={t('sidebar.canvas')}
                    style={{
                      width: '19px',
                      height: '19px',
                    }}
                  />
                </div>
                <div
                  onClick={() => handleProtectedNavigation('/canvas-projects', t('sidebar.canvas'), { showSidebar: true })}
                  onMouseEnter={e => {
                    if (!isPathActive('/canvas-projects')) {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                    }
                  }}
                  onMouseLeave={e => {
                    if (!isPathActive('/canvas-projects')) {
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '197px',
                    height: '40px',
                    flexShrink: 0,
                    borderRadius: '10px',
                    background: isPathActive('/canvas-projects')
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'transparent',
                    padding: '12px 16px 12px 35px',
                    boxSizing: 'border-box',
                    textDecoration: 'none',
                    marginBottom: '4px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <span
                    style={{
                      color: isPathActive('/canvas-projects') ? '#FF6D49' : '#FFF',
                      fontFeatureSettings: "'liga' off, 'clig' off",
                      fontSize: '14px',
                      fontStyle: 'normal',
                      fontWeight: isPathActive('/canvas-projects') ? 600 : 400,
                      lineHeight: '16px',
                    }}
                  >
                    {t('sidebar.canvas')}
                  </span>
                </div>
              </div>

              {/* 专家&Skills（紧挨创意画布下方） */}
              <div style={{ position: 'relative', marginBottom: '4px' }}>
                <div
                  style={{
                    position: 'absolute',
                    left: '17px',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 2,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <ExpertSkillsNavIcon color={isPathActive('/expert-skills') ? '#FF6D49' : '#FFF'} />
                </div>
                <div
                  onClick={() => handleProtectedNavigation('/expert-skills', t('sidebar.expertSkills'))}
                  onMouseEnter={e => {
                    if (!isPathActive('/expert-skills')) {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                    }
                  }}
                  onMouseLeave={e => {
                    if (!isPathActive('/expert-skills')) {
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '197px',
                    height: '40px',
                    flexShrink: 0,
                    borderRadius: '10px',
                    background: isPathActive('/expert-skills')
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'transparent',
                    padding: '12px 16px 12px 35px',
                    boxSizing: 'border-box',
                    textDecoration: 'none',
                    marginBottom: '4px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <span
                    style={{
                      color: isPathActive('/expert-skills') ? '#FF6D49' : '#FFF',
                      fontFeatureSettings: "'liga' off, 'clig' off",
                      fontSize: '14px',
                      fontStyle: 'normal',
                      fontWeight: isPathActive('/expert-skills') ? 600 : 400,
                      lineHeight: '16px',
                    }}
                  >
                    {t('sidebar.expertSkills')}
                  </span>
                </div>
              </div>

              {/* 文本创作 */}
              <div style={{ position: 'relative', marginBottom: '4px' }}>
                <div
                  style={{
                    position: 'absolute',
                    left: '17px',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 2,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <img
                    src={isPathActive('/creation/text') ? ActiveTextIcon : InactiveTextIcon}
                    alt={t("sidebar.textCreation")}
                    style={{
                      width: '20px',
                      height: '20px',
                    }}
                  />
                </div>

                <div
                  onClick={() => handleProtectedNavigation('/creation/text', t('sidebar.textCreation'))}
                  onMouseEnter={() => handleMouseEnter('/creation/text')}
                  onMouseLeave={handleMouseLeave}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '197px',
                    height: '40px',
                    flexShrink: 0,
                    borderRadius: '10px',
                    background: isPathActive('/creation/text')
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'transparent',
                    padding: '12px 16px 12px 35px',
                    boxSizing: 'border-box',
                    textDecoration: 'none',
                    marginBottom: '4px',
                    transition: 'all 0.2s ease',
                    cursor: 'pointer',
                  }}
                >
                  <span
                    style={{
                      color: isPathActive('/creation/text') ? '#FF6D49' : '#FFF',
                      fontFeatureSettings: "'liga' off, 'clig' off",
                      fontSize: '14px',
                      fontStyle: 'normal',
                      fontWeight: 600,
                      lineHeight: '16px',
                    }}
                  >
                    {t('sidebar.textCreation')}
                  </span>
                  <span
                    style={{
                      position:'absolute',
                      right:'0',
                      padding: '0px 6px 2px',
                      borderRadius: '6px',
                      border: '1px solid #FF6D49',
                      background: 'rgba(255, 109, 73, 0.3)',
                      flexShrink: 0,
                    }}
                  >
                    <span style={{ color: '#FFF', fontSize: '10px', fontWeight: 400, lineHeight: '14px', whiteSpace: 'nowrap' }}>{t('sidebar.promptReverse')}</span>
                    <span style={{color: '#FFF', background: '#FF6D49', fontSize: '8px', fontWeight: 700, lineHeight: '14px',borderRadius:'2px' ,padding:'0 2px',marginLeft:'4px'}}>NEW</span>
                  </span>
                </div>
              </div>

              {/* 图片创作 */}
              <div style={{ position: 'relative', marginBottom: '4px' }}>
                <div
                  style={{
                    position: 'absolute',
                    left: '17px',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 2,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <img
                    src={isPathActive('/creation/image') ? ActiveImageIcon : InactiveImageIcon}
                    alt={t("sidebar.imageCreation")}
                    style={{
                      width: '17px',
                      height: '17px',
                    }}
                  />
                </div>

                <div
                  onClick={() => handleProtectedNavigation('/creation/image', t('sidebar.imageCreation'))}
                  onMouseEnter={() => handleMouseEnter('/creation/image')}
                  onMouseLeave={handleMouseLeave}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '197px',
                    height: '40px',
                    flexShrink: 0,
                    borderRadius: '10px',
                    background: isPathActive('/creation/image')
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'transparent',
                    padding: '12px 16px 12px 35px',
                    boxSizing: 'border-box',
                    textDecoration: 'none',
                    marginBottom: '4px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <span
                    style={{
                      color: isPathActive('/creation/image') ? '#FF6D49' : '#FFF',
                      fontFeatureSettings: "'liga' off, 'clig' off",
                      fontSize: '14px',
                      fontStyle: 'normal',
                      fontWeight: isPathActive('/creation/image') ? 600 : 400,
                      lineHeight: '16px',
                    }}
                  >
                    {t('sidebar.imageCreation')}
                  </span>
                </div>
              </div>

              {/* 视频创作 */}
              <div style={{ position: 'relative', marginBottom: '4px' }}>
                <div
                  style={{
                    position: 'absolute',
                    left: '17px',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 2,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <img
                    src={isPathActive('/creation/video') ? ActiveVideoIcon : InactiveVideoIcon}
                    alt={t("sidebar.videoCreation")}
                    style={{
                      width: '19px',
                      height: '19px',
                    }}
                  />
                </div>

                <div
                  onClick={() => handleProtectedNavigation('/creation/video', t('sidebar.videoCreation'))}
                  onMouseEnter={() => handleMouseEnter('/creation/video')}
                  onMouseLeave={handleMouseLeave}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '197px',
                    height: '40px',
                    flexShrink: 0,
                    borderRadius: '10px',
                    background: isPathActive('/creation/video')
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'transparent',
                    padding: '12px 16px 12px 35px',
                    boxSizing: 'border-box',
                    textDecoration: 'none',
                    marginBottom: '4px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <span
                    style={{
                      color: isPathActive('/creation/video') ? '#FF6D49' : '#FFF',
                      fontFeatureSettings: "'liga' off, 'clig' off",
                      fontSize: '14px',
                      fontStyle: 'normal',
                      fontWeight: isPathActive('/creation/video') ? 600 : 400,
                      lineHeight: '16px',
                    }}
                  >
                    {t('sidebar.videoCreation')}
                  </span>
                </div>
              </div>

              {/* 视频剪辑（品牌可经 hiddenSidebarItems 隐藏，如 juxflow 站点） */}
              {!hiddenSidebarItems.includes('videoEdit') && (
              <div style={{ position: 'relative', marginBottom: '4px' }}>
                {/* 图标定位在红线上 */}
                <div
                  style={{
                    position: 'absolute',
                    left: '17px', // 调整为32px以补偿父容器的8px padding
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 2,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <img
                    src={
                      isPathActive('/creation/edit-video')
                        ? VideoEditActive
                        : VideoEdit
                    }
                    alt={t("sidebar.videoEdit")}
                    style={{
                      width: '19px',
                      height: '19px',
                    }}
                  />
                </div>

                <div
                  onClick={() => handleProtectedNavigation('/creation/edit-video', t('sidebar.videoEdit'))}
                  onMouseEnter={() => handleMouseEnter('/creation/edit-video')}
                  onMouseLeave={handleMouseLeave}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '197px',
                    height: '40px',
                    flexShrink: 0,
                    borderRadius: '10px',
                    background: isPathActive('/creation/edit-video')
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'transparent',
                    padding: '12px 16px 12px 35px', // 增加左侧内边距给图标空间
                    boxSizing: 'border-box',
                    textDecoration: 'none',
                    marginBottom: '4px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <span
                    style={{
                      color: isPathActive('/creation/edit-video') ? '#FF6D49' : '#FFF',
                      fontFeatureSettings: "'liga' off, 'clig' off",
                      fontSize: '14px',
                      fontStyle: 'normal',
                      fontWeight: isPathActive('/creation/edit-video') ? 600 : 400,
                      lineHeight: '16px',
                    }}
                  >
                    {t('sidebar.videoEdit')}
                  </span>
                </div>
              </div>
              )}

              {/* 小工具 */}
              <div style={{ position: 'relative', marginBottom: '4px' }}>
                {/* 图标定位在红线上 */}
                <div
                  style={{
                    position: 'absolute',
                    left: '17px', // 调整为32px以补偿父容器的8px padding
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 2,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <img
                    src={isPathActive('/creation/tools') ? ActiveToolsIcon : InactiveToolsIcon}
                    alt={t("sidebar.tools")}
                    style={{
                      width: '19px',
                      height: '19px',
                    }}
                  />
                </div>

                <div
                  onClick={() => handleProtectedNavigation('/creation/tools', t('sidebar.tools'))}
                  onMouseEnter={() => handleMouseEnter('/creation/tools')}
                  onMouseLeave={handleMouseLeave}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '197px',
                    height: '40px',
                    flexShrink: 0,
                    borderRadius: '10px',
                    background: isPathActive('/creation/tools')
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'transparent',
                    padding: '12px 16px 12px 35px',
                    boxSizing: 'border-box',
                    textDecoration: 'none',
                    marginBottom: '4px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <span
                    style={{
                      color: isPathActive('/creation/tools') ? '#FF6D49' : '#FFF',
                      fontFeatureSettings: "'liga' off, 'clig' off",
                      fontSize: '14px',
                      fontStyle: 'normal',
                      fontWeight: isPathActive('/creation/tools') ? 600 : 400,
                      lineHeight: '16px',
                    }}
                  >
                    {t('sidebar.tools')}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
        )}

        {/* 横向分隔线（AI创作区块隐藏时此线去掉，避免与下一条相邻成双线） */}
        {!hiddenSidebarSections.includes('creation') && (
          <div style={{ height: '1px', backgroundColor: borderColor, margin: '16px 0' }} />
        )}

        {/* 画布 — 已移至 AmeTV 位置，此处隐藏 */}
        {/* <div style={{ marginBottom: '8px', position: 'relative' }}>
          ...
        </div> */}

        {/* 资产 */}
        <div style={{ marginBottom: '8px', position: 'relative' }}>
          <div
            style={{
              position: 'absolute',
              left: '24px',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 2,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <img
              src={isPathActive('/assets') ? ActiveAssetsIcon : InactiveAssetsIcon}
              alt={t("sidebar.assets")}
              style={{
                width: '17px',
                height: '17px',
              }}
            />
          </div>

          <div
            onClick={() => handleProtectedNavigation('/assets', t('sidebar.assets'))}
            onMouseEnter={() => handleMouseEnter('/assets')}
            onMouseLeave={handleMouseLeave}
            style={{
              display: 'flex',
              alignItems: 'center',
              width: '197px',
              height: '40px',
              flexShrink: 0,
              borderRadius: '10px',
              background: isPathActive('/assets') ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
              padding: '12px 16px 12px 44px', // 增加左侧内边距给图标空间
              boxSizing: 'border-box',
              textDecoration: 'none',
              transition: 'all 0.2s ease',
              cursor: 'pointer',
            }}
          >
            <span
              style={{
                color: isPathActive('/assets') ? '#FF6D49' : '#FFF',
                fontFeatureSettings: "'liga' off, 'clig' off",
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: isPathActive('/assets') ? 600 : 400,
                lineHeight: '16px',
              }}
            >
              {t('sidebar.assets')}
            </span>
          </div>
        </div>

        {/* 任务 */}
        <div style={{ marginBottom: '8px', position: 'relative' }}>
          {/* 图标定位在红线上 */}
          <div
            style={{
              position: 'absolute',
              left: '24px',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 2,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 21 21"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3.5 5.00293C3.50003 4.59066 3.97125 4.35576 4.30078 4.60352L6.95605 6.59961C7.08146 6.69402 7.1552 6.84205 7.15527 6.99902V16.4971C7.15525 16.9093 6.68403 17.1442 6.35449 16.8965L3.69922 14.9004C3.57377 14.806 3.50007 14.658 3.5 14.501V5.00293Z"
                stroke={isPathActive('/tasks') ? '#FF6D49' : '#FFF'}
              />
              <path
                d="M8.67236 16.4971C8.67239 16.9093 9.14361 17.1442 9.47314 16.8965L12.1284 14.9004C12.2538 14.806 12.3276 14.658 12.3276 14.501V5.00293C12.3276 4.59066 11.8564 4.35575 11.5269 4.60352L8.87158 6.59961C8.74614 6.69402 8.67244 6.84202 8.67236 6.99902V16.4971Z"
                stroke={isPathActive('/tasks') ? '#FF6D49' : '#FFF'}
              />
              <path
                d="M17.6553 16.4971C17.6552 16.9093 17.184 17.1442 16.8545 16.8965L14.1992 14.9004C14.0738 14.806 14.0001 14.658 14 14.501V5.00293C14 4.59066 14.4712 4.35575 14.8008 4.60352L17.4561 6.59961C17.5815 6.69402 17.6552 6.84202 17.6553 6.99902V16.4971Z"
                stroke={isPathActive('/tasks') ? '#FF6D49' : '#FFF'}
              />
            </svg>
          </div>

          <div
            onClick={() => handleProtectedNavigation('/tasks', t('sidebar.taskHall'), { showSidebar: true })}
            onMouseEnter={e => {
              handleMouseEnter('/tasks');
              if (!isPathActive('/tasks')) {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
              }
            }}
            onMouseLeave={e => {
              handleMouseLeave();
              if (!isPathActive('/tasks')) {
                e.currentTarget.style.background = 'transparent';
              }
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              width: '197px',
              height: '40px',
              flexShrink: 0,
              borderRadius: '10px',
              background: isPathActive('/tasks')
                ? 'rgba(255, 255, 255, 0.05)'
                : 'transparent',
              padding: '12px 16px 12px 44px', // 增加左侧内边距给图标空间
              boxSizing: 'border-box',
              textDecoration: 'none',
              transition: 'all 0.2s ease',
              cursor: 'pointer',
            }}
          >
            <span
              style={{
                color: isPathActive('/tasks') ? '#FF6D49' : '#FFF',
                fontFeatureSettings: "'liga' off, 'clig' off",
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: isPathActive('/tasks') ? 600 : 400,
                lineHeight: '16px',
              }}
            >
              {t('sidebar.taskHall')}
            </span>
          </div>
        </div>

        {/* 横向分隔线 */}
        {!hiddenSidebarSections.includes('ricegrain-divider') && (
        <div style={{ height: '1px', backgroundColor: borderColor, margin: '16px 0' }} />
        )}
        {/* 米粒值 */}
        {!hiddenSidebarSections.includes('ricegrain') && (
        <div style={{ marginBottom: '8px', position: 'relative' }}>
          <div
            style={{
              position: 'absolute',
              left: '24px',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 2,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <img
              src={AssetsIconRicegrainvalue}
              alt={t("sidebar.ricegrain")}
              style={{
                width: '17px',
                height: '17px',
              }}
            />
          </div>

          <div
            onClick={() => handleProtectedNavigation('/ricegrain/purchase', t('sidebar.ricegrain'))}
            onMouseEnter={() => handleMouseEnter('/ricegrain/purchase')}
            onMouseLeave={handleMouseLeave}
            style={{
              display: 'flex',
              alignItems: 'center',
              width: '197px',
              height: '40px',
              flexShrink: 0,
              borderRadius: '10px',
              background: isPathActive('/ricegrain/purchase')
                ? 'rgba(255, 255, 255, 0.05)'
                : 'transparent',
              padding: '12px 16px 12px 44px', // 增加左侧内边距给图标空间
              boxSizing: 'border-box',
              textDecoration: 'none',
              transition: 'all 0.2s ease',
              cursor: 'pointer',
            }}
          >
            <span
              style={{
                color: isPathActive('/ricegrain/purchase') ? '#FF6D49' : '#FFF',
                fontFeatureSettings: "'liga' off, 'clig' off",
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: isPathActive('/ricegrain/purchase') ? 600 : 400,
                lineHeight: '16px',
              }}
            >
              {t('sidebar.ricegrain')}
            </span>
          </div>
        </div>
        )}
        {/* 会员 */}
        {!hiddenSidebarSections.includes('membership') && (
        <div style={{ marginBottom: '8px', position: 'relative' }}>
          <div
            style={{
              position: 'absolute',
              left: '24px',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 2,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <img
              src={MemberIcon}
              alt={t("sidebar.member")}
              style={{
                width: '17px',
                height: '17px',
              }}
            />
          </div>

          <div
            onClick={() => handleProtectedNavigation('/ricegrain/membership', t('sidebar.member'))}
            onMouseEnter={() => handleMouseEnter('/ricegrain/membership')}
            onMouseLeave={handleMouseLeave}
            style={{
              display: 'flex',
              alignItems: 'center',
              width: '197px',
              height: '40px',
              flexShrink: 0,
              borderRadius: '10px',
              background: isPathActive('/ricegrain/membership')
                ? 'rgba(255, 255, 255, 0.05)'
                : 'transparent',
              padding: '12px 16px 12px 44px',
              boxSizing: 'border-box',
              textDecoration: 'none',
              transition: 'all 0.2s ease',
              cursor: 'pointer',
            }}
          >
            <span
              style={{
                color: isPathActive('/ricegrain/membership') ? '#FF6D49' : '#FFF',
                fontFeatureSettings: "'liga' off, 'clig' off",
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: isPathActive('/ricegrain/membership') ? 600 : 400,
                lineHeight: '16px',
              }}
            >
              {t('sidebar.member')}
            </span>
          </div>
        </div>
        )}
        {/* 横向分隔线 */}
        <div style={{ height: '1px', backgroundColor: borderColor, margin: '16px 0' }} />

        {!hiddenSidebarItems.includes('invite') && !isTeamSpace && (
        <div style={{ marginBottom: '8px', position: 'relative' }}>
          {isLoggedIn ? (
            // 已登录且选中：橙色背景按钮，居中
            <div
              onClick={() => handleProtectedNavigation('/invite', t('sidebar.inviteFriends'), { showSidebar: true })}
              onMouseEnter={() => handleMouseEnter('/invite')}
              onMouseLeave={handleMouseLeave}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                width: '197px',
                height: '40px',
                borderRadius: '10px',
                background: 'linear-gradient(90deg, #FF6D49 0%, #FF8A6C 100%)',
                boxSizing: 'border-box',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(255, 109, 73, 0.2)',
                transition: 'all 0.3s ease',
              }}
              onMouseOver={e => {
                e.currentTarget.style.background = 'linear-gradient(90deg, #FF5A36 0%, #FF7759 100%)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 109, 73, 0.4)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseOut={e => {
                e.currentTarget.style.background = 'linear-gradient(90deg, #FF6D49 0%, #FF8A6C 100%)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(255, 109, 73, 0.2)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <img
                src={InviteIcon}
                alt={t("sidebar.inviteFriends")}
                style={{ width: '18px', height: '18px', filter: 'brightness(0) invert(1)', flexShrink: 0 }}
              />
              <span
                style={{
                  color: '#FFFFFF',
                  fontFeatureSettings: "'liga' off, 'clig' off",
                  fontSize: '15px',
                  fontStyle: 'normal',
                  lineHeight: '18px',
                  letterSpacing: '0.5px',
                }}
              >
                {t('sidebar.inviteFriends')}
              </span>
            </div>
          ) : (
            // 未登录 或 未选中：无背景，橙色图标+橙色文字，左对齐
            <div
              onClick={() => handleProtectedNavigation('/invite', t('sidebar.inviteFriends'), { showSidebar: true })}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                width: '197px',
                height: '40px',
                padding: '0 16px',
                boxSizing: 'border-box',
                cursor: 'pointer',
              }}
            >
              <img
                src={InviteIcon}
                alt={t("sidebar.inviteFriends")}
                style={{
                  width: '18px',
                  height: '18px',
                  flexShrink: 0,
                  filter: 'brightness(0) saturate(100%) invert(55%) sepia(80%) saturate(600%) hue-rotate(330deg) brightness(105%)',
                }}
              />
              <span
                style={{
                  color: '#FF6D49',
                  fontFeatureSettings: "'liga' off, 'clig' off",
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: '16px',
                }}
              >
                {t('sidebar.inviteFriends')}
              </span>
            </div>
          )}
        </div>
        )}
      </div>

      {/* ICP 备案信息（品牌可配，如 gxzs 站点）：底部用户信息上方展示，跳工信部备案系统 */}
      {icpRecord && (
        <a
          href={icpRecord.url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'block',
            flexShrink: 0,
            padding: '8px 0 16px',
            background: '#101010',
            color: 'rgba(255, 255, 255, 0.35)',
            fontSize: '11px',
            lineHeight: '16px',
            textAlign: 'center',
            textDecoration: 'none',
            transition: 'color 0.2s ease',
          }}
          onMouseEnter={e => { e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)'; }}
          onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255, 255, 255, 0.35)'; }}
        >
          {icpRecord.text}
        </a>
      )}

      {/* 底部边框线：置于备案标签与底部用户信息之间（未配置备案时紧贴用户信息上方，与原布局一致） */}
      <div
        style={{
          height: '1px',
          background: borderColor,
          margin: '0 16px',
        }}
      ></div>

      {/* 底部用户信息：juxflow 等品牌未登录时整个组件隐藏（showLoginRegisterButton=false 且未登录） */}
      {/* V0.1 团队协作：已登录替换为 AccountMenu（两行账户卡+点击浮层）；未登录保留登录按钮 */}
      {(isLoggedIn || showLoginRegisterButton !== false) && (
        isLoggedIn ? (
          <AccountMenu />
        ) : (
      <div
        style={{
          padding: '16px 24px',
          display: 'flex',
          alignItems: 'center',
          height: '80px',
          boxSizing: 'border-box',
          background: '#101010',
          flexShrink: 0,
          position: 'relative',
        }}
        onMouseEnter={() => setIsUserHovered(true)}
        onMouseLeave={() => setIsUserHovered(false)}
      >
        {/* 用户信息容器 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            width: '197px',
            height: '42px',
            borderRadius: '8px',
            background: isLoggedIn && isUserHovered ? '#1A1A1A' : 'transparent',
            boxSizing: 'border-box',
            transition: 'background 0.2s ease',
            cursor: 'pointer',
          }}
        >
          {isLoggedIn ? (
            <>
              <Avatar
                size={28}
                src={userInfo?.user_avatar || AvatarIcon}
                style={{
                  backgroundColor: '#333',
                  color: '#999',
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  color: inactiveTextColor,
                  fontSize: '14px',
                  marginLeft: '12px',
                  fontWeight: '500',
                }}
              >
                {userInfo?.mobile
                  ? maskMobile(userInfo.mobile)
                  : userInfo?.auth_identifier || t('user.defaultName')}
              </span>
            </>
          ) : showLoginRegisterButton !== false ? (
            <button
              className="login-redict"
              onClick={() => eventBus.emit('showLoginModal')}
              style={{
                borderRadius: "10px",
                background: "linear-gradient(122.95deg, #feae45, #ff6d49 68.75%)",
                padding: '5px 16px',
                border: 'none',
                cursor: 'pointer',
                color: '#fff',
                width: '100%',
                height: '42px',
              }}
            >
              {t('login.loginRegister')}
            </button>
          ) : null}
        </div>

        {/* 退出登录按钮 - 仅在已登录且悬停时显示 */}
        {isLoggedIn && isUserHovered && (
          <div style={userMenuPopupStyles.container}>
            <div style={userMenuPopupStyles.menuItem} onClick={() => navigate('/release-notes')}>
              <img src={AlarmIcon} alt={t("sidebar.updateAnnouncement")} style={{ ...userMenuPopupStyles.avatar, filter: 'brightness(0) invert(1)' }} />
              <span style={userMenuPopupStyles.text}>{t('sidebar.updateAnnouncement')}</span>
            </div>
            {!hiddenUserMenuItems.includes('feedback') && (
            <div
              style={{
                ...userMenuPopupStyles.menuItem,
                position: 'relative',
              }}
              onMouseEnter={() => setIsFeedbackHovered(true)}
              onMouseLeave={() => setIsFeedbackHovered(false)}
            >
              <img src={QAIcon} alt={t("sidebar.feedback")} style={userMenuPopupStyles.avatar} />
              <span style={userMenuPopupStyles.text}>{t('sidebar.feedback')}</span>

              {/* 二维码弹窗 */}
              {isFeedbackHovered && (
                <div
                  style={{
                    position: 'absolute',
                    left: '100%',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    marginLeft: '24px',
                    zIndex: 10000,
                  }}
                  onMouseEnter={() => setIsFeedbackHovered(true)}
                  onMouseLeave={() => setIsFeedbackHovered(false)}
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* 弹窗容器 */}
                  <div
                    style={{
                      width: '196px',
                      height: '238px',
                      background: '#1a1a1a',
                      borderRadius: '12px',
                      boxShadow: `
                        0 8px 32px rgba(0, 0, 0, 0.4),
                        3px 3px 8px 3px rgba(255, 109, 73, 0.1),
                        0 0 0 1px rgba(255, 109, 73, 0.3),
                        0 0 20px rgba(255, 109, 73, 0.2),
                        0 0 40px rgba(255, 109, 73, 0.1)
                      `,
                      padding: '0',
                      display: 'flex',
                      flexDirection: 'column',
                      position: 'relative',
                    }}
                  >
                    {/* 标题区域 */}
                    <div
                      style={{
                        padding: '18px 18px 14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        textAlign: 'center',
                      }}
                    >
                      <img
                        src={QAInputIcon}
                        alt={t("sidebar.feedback")}
                        style={{
                          width: '20px',
                          height: '20px',
                        }}
                      />
                      <span
                        style={{
                          color: '#FFF',
                          fontSize: '16px',
                          fontStyle: 'normal',
                          fontWeight: 500,
                          lineHeight: '24px',
                        }}
                      >
                        {t('common.scanQRFeedback')}
                      </span>
                    </div>

                    {/* 二维码区域 */}
                    <div
                      style={{
                        margin: '0 18px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      
                      <div
                        style={{
                          width: '160px',
                          height: '160px',
                          background: '#FFF',
                          borderRadius: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative',
                          overflow: 'hidden',
                        }}
                      >
                        {/* 微信二维码图片 */}
                        <img
                          src={WeChatQRCode}
                          alt={t("sidebar.wechatQR")}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: '12px',
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            )}
            {!hiddenUserMenuItems.includes('profile') && (
            <div style={userMenuPopupStyles.menuItem} onClick={() => goToRoute('/user')}>
              <img src={UserAvatarIcon} alt={t("user.avatar")} style={userMenuPopupStyles.avatar} />
              <span style={userMenuPopupStyles.text}>{t('sidebar.personalCenter')}</span>
            </div>
            )}
            {!hiddenUserMenuItems.includes('agreements') && (
            <div style={userMenuPopupStyles.menuItem} onClick={goToRule}>
              <svg
                viewBox="0 0 1024 1024"
                version="1.1"
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                style={userMenuPopupStyles.icon}
              >
                <path
                  d="M853.333333 938.666667H170.666667a42.666667 42.666667 0 0 1-42.666667-42.666667V128a42.666667 42.666667 0 0 1 42.666667-42.666667h682.666666a42.666667 42.666667 0 0 1 42.666667 42.666667v768a42.666667 42.666667 0 0 1-42.666667 42.666667z m-42.666666-85.333334V170.666667H213.333333v682.666666h597.333334zM341.333333 298.666667h341.333334v85.333333H341.333333V298.666667z m0 170.666666h341.333334v85.333334H341.333333v-85.333334z m0 170.666667h213.333334v85.333333H341.333333v-85.333333z"
                  fill="#ffffff"
                ></path>
              </svg>
              <span style={userMenuPopupStyles.text}>{t('sidebar.relatedAgreements')}</span>
            </div>
            )}
            <div style={userMenuPopupStyles.menuItem} onClick={handleLogout}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                style={userMenuPopupStyles.icon}
              >
                <path
                  d="M7.99723 2H2V14H8"
                  stroke="white"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M11 11L14 8L11 5"
                  stroke="white"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M5.33334 7.99707H14"
                  stroke="white"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span style={userMenuPopupStyles.text}>{t('sidebar.logout')}</span>
            </div>
          </div>
        )}
      </div>
      )
      )}
    </div>
  </>
  );
};

export default AgreementSidebar;
